'use client';
import DocumentCard from '@/app/[locale]/(protected)/(dashboard)/ui/DocumentCard';
import { ITabConfig } from '@/app/[locale]/(protected)/(dashboard)/ui/TabTitleRender';
import EntityTag from '@atoms/EntityTag';
import useInfiniteScrollSearch from '@common/application/hooks/useTagSearch/useInfiniteScrollSearch';
import { DocumentClient } from '@common/model/apiClient';
import { Document } from '@common/model/apiClient/Document';
import { Tag } from '@common/model/apiClient/Tag';
import TagMenu, { TagWithCategory } from '@molecules/TagMenu';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ScrollView } from 'devextreme-react';
import React, { useEffect } from 'react';
import styles from './TabView.module.css';

const searchSize = 10;

const DocumentTabView = (props: { data: ITabConfig<Document>; selectedTags: TagWithCategory[]; setSelectedTags: React.Dispatch<React.SetStateAction<TagWithCategory[]>> }) => {
  const { id } = props.data;
  const isTagsView = id === 'tags';
  const viewClass = clsx(styles.view, { [styles.cardsView]: false });
  const { selectedTags, setSelectedTags } = props.data;

  const {
    search: docTagSearch,
    setSearch: setDocTagSearch,
    fetchNext: fetchNextDocTags,
    updateResults: updateDocTags,
    hasMore: hasMoreDocTags,
    searchAfter: docTagSearchAfter,
    refetch: refetchDocTags,
    items: tags,
    setItems,
  } = useInfiniteScrollSearch<TagWithCategory>({ size: searchSize, queryKey: 'document_tag_query', getSearchAfter: (item) => [item.category?.name, item.label] });

  const onAddTag = (tag: TagWithCategory) => {
    if (!setSelectedTags) {
      throw new Error('setSelectedTags not defined');
    }
    setSelectedTags((tags) => [...tags, tag]);
  };

  const onRemoveTag = (tag: TagWithCategory) => {
    if (!setSelectedTags) {
      throw new Error('setSelectedTags not defined');
    }
    setSelectedTags((tags) => tags.filter((t) => t.id !== tag.id));
  };

  const { data: docTagData } = useQuery({
    queryKey: ['doc_tag_query', docTagSearch, docTagSearchAfter],
    queryFn: () => Tag.list('document', docTagSearch, {}, searchSize, docTagSearchAfter),
  });

  const { data: taggedDocData, isFetching: isTaggedDocsFetching } = useQuery({
    queryKey: ['Docs_home_search', selectedTags],
    // () => DocClient.list('', {}, { deleted: false, musts: selectedDocTags.map((t) => ({ terms: { tags: [`${t.id}`] } })) }, 10),
    queryFn: () => DocumentClient.getByTags(selectedTags!.map((t) => t.id)),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    placeholderData: (previousData) => previousData,
    enabled: !!selectedTags?.length,
  });

  const data = isTagsView ? (taggedDocData?.payload ?? []) : props.data.data;

  useEffect(() => {
    if (docTagData?.data?.payload) {
      updateDocTags(docTagData?.data?.payload);
    }
  }, [docTagData?.data?.payload]);

  return (
    <div className={styles.wrapper}>
      <ScrollView showScrollbar={'onHover'} width={'100%'}>
        <div className={viewClass}>
          {data.map((doc) => (
            <DocumentCard document={doc} key={doc.id} />
          ))}
        </div>
      </ScrollView>
      {/*Until I come up with a better idea we're stuck with either this string equality check, or the overly convoluted 'QuickNav' solution it is replacing*/}
      {isTagsView && (
        <div className={styles.sidebar}>
          {/*<PrefixedInput icon={<FontAwesomeIcon icon={['far', 'search']} />}>*/}
          {/*  <Input onChange={(value) => setSearch(value)} value={search} />*/}
          {/*</PrefixedInput>*/}
          <div>
            <TagMenu
              tags={tags}
              search={docTagSearch}
              setSearch={setDocTagSearch}
              fetchNext={fetchNextDocTags}
              id={'DocDashboardTagMenu'}
              hasMore={hasMoreDocTags}
              icon={'plus'}
              onSelect={(tag: TagWithCategory) => {
                onAddTag(tag);
              }}
            />
          </div>
          <div className={styles.selectedTags}>
            {selectedTags?.map((tag) => (
              <div className={styles.tagWrapper} key={`tag${tag.id}`}>
                <EntityTag
                  label={tag.label}
                  style={tag.category.style}
                  category={tag.category.name}
                  button={'multiply'}
                  onClick={() => {
                    onRemoveTag(tag);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTabView;
